import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

import StandingsTableV2 from '../../../components/StandingsTableV2';
import CategoryTabs from '../format/components/CategoryTabs';
import EmptyState from '../../../components/Standings/EmptyState';
import SPORT_TYPES from '../../../utils/sportTypes';
import ClubbieLoader from '../../../components/Loader/ClubbieLoader';
import teamBadgePlaceholder from '../../../assets/images/team_badge_placeholder.svg';

const ReadOnlySlot = ({ team, score, hasResult, isWinner }) => (
  <div className={`w-full px-[16px] py-[8px] rounded-full flex justify-between items-center ${isWinner ? 'bg-app-card' : 'bg-[#021529]'}`}>
    <div className="flex gap-[8px] items-center mr-2">
      {team?.teamId ? (
        <>
          <img
            src={team.crest || teamBadgePlaceholder}
            alt={team.title}
            className="w-5 h-5 rounded-full object-cover shrink-0 border border-[#192634]"
            onError={(e) => { e.target.src = teamBadgePlaceholder; }}
          />
          <span className="text-[#E5E5D6] text-[16px] font-semibold">{team.title}</span>
        </>
      ) : (
        <span className="text-[#B2B5AD] text-[16px] font-semibold">TBD</span>
      )}
    </div>
    {hasResult && (
      <span className="text-[#E5E5D6] text-[14px] font-semibold">{score ?? 0}</span>
    )}
  </div>
);

const FixtureCard = ({ fixture, name }) => {
  const { homeTeam, awayTeam, homeTeamScore, awayTeamScore, hasResult } = fixture;
  const homeWins = hasResult && homeTeamScore != null && awayTeamScore != null && homeTeamScore > awayTeamScore;
  const awayWins = hasResult && homeTeamScore != null && awayTeamScore != null && awayTeamScore > homeTeamScore;

  return (
    <div className="inline-flex flex-col items-start gap-[12px] px-[16px] py-[12px] border border-[#192634] rounded-[20px] bg-[#011224] w-[264px]">
      <div className="w-full flex justify-between items-center">
        <span className="text-[#B2B5AD] text-[14px] font-semibold">{name}</span>
      </div>
      <div className="w-full flex flex-col gap-[12px]">
        <ReadOnlySlot team={homeTeam} score={homeTeamScore} hasResult={hasResult} isWinner={homeWins} />
        <ReadOnlySlot team={awayTeam} score={awayTeamScore} hasResult={hasResult} isWinner={awayWins} />
      </div>
    </div>
  );
};

const TieCard = ({ legs, name }) => {
  // Aggregate scores across legs
  const allCompleted = legs.every((l) => l.hasResult);
  const homeAggregate = legs.reduce((sum, l) => sum + (l.homeTeamScore || 0), 0);
  const awayAggregate = legs.reduce((sum, l) => sum + (l.awayTeamScore || 0), 0);
  const homeWinsAgg = allCompleted && homeAggregate > awayAggregate;
  const awayWinsAgg = allCompleted && awayAggregate > homeAggregate;

  // Use first leg's teams for display
  const { homeTeam, awayTeam } = legs[0];

  return (
    <div className="inline-flex flex-col items-start gap-[8px] px-[16px] py-[12px] border border-[#192634] rounded-[20px] bg-[#011224] w-[264px]">
      <div className="w-full flex justify-between items-center">
        <span className="text-[#B2B5AD] text-[14px] font-semibold">{name}</span>
        <span className="text-[#00E7FF] text-[11px] font-medium bg-[#00E7FF]/10 px-2 py-0.5 rounded-full">
          {legs.length} legs
        </span>
      </div>

      {/* Aggregate */}
      <div className="w-full flex flex-col gap-[8px]">
        <ReadOnlySlot team={homeTeam} score={homeAggregate} hasResult={allCompleted} isWinner={homeWinsAgg} />
        <ReadOnlySlot team={awayTeam} score={awayAggregate} hasResult={allCompleted} isWinner={awayWinsAgg} />
      </div>

      {/* Per-leg scores */}
      <div className="w-full border-t border-[#192634] pt-2 flex flex-col gap-1">
        {legs.map((leg, i) => (
          <div key={leg.fixtureId || i} className="flex justify-between items-center text-[12px] text-[#7F8585]">
            <span>Leg {leg.leg || i + 1}</span>
            {leg.hasResult ? (
              <span className="text-[#B2B5AD]">{leg.homeTeamScore} - {leg.awayTeamScore}</span>
            ) : (
              <span>-</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Group fixtures by tieNumber for multi-leg brackets
const groupFixturesByTie = (fixtures) => {
  if (!fixtures?.length) return [];
  const hasMultiLeg = fixtures.some((f) => f.tieNumber != null);
  if (!hasMultiLeg) return null; // signal to use FixtureCard per fixture

  const tieMap = new Map();
  fixtures.forEach((f) => {
    const key = f.tieNumber;
    if (!tieMap.has(key)) tieMap.set(key, []);
    tieMap.get(key).push(f);
  });
  return Array.from(tieMap.values()).map((legs) =>
    legs.sort((a, b) => (a.leg || 1) - (b.leg || 1))
  );
};

const StandingsViewV2 = ({
  phases = [],
  loading = false,
  selectedCategory,
  selectedTournament,
  sportType = SPORT_TYPES.FOOTBALL,
  onCategoryChange,
}) => {
  const isEmpty = !loading && (!phases || phases.length === 0);
  const [collapsedPhases, setCollapsedPhases] = useState({});

  // Reset collapsed state when phases change (e.g. category switch)
  useEffect(() => {
    setCollapsedPhases({});
  }, [phases]);

  const togglePhase = (phaseId) => {
    setCollapsedPhases((prev) => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  return (
    <div className={`p-3 sm:p-[18px] lg:px-8 flex flex-col gap-5 ${isEmpty ? "h-full" : ""}`}>
      <div className="flex items-center justify-between">
        <h1 className="text-[22px] font-normal text-[#FEFCEB]">Standings</h1>
        <div className="flex items-center gap-3 opacity-0">
          <div className="px-4 py-2 text-[15px] font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors">
            dummy
          </div>
          <button className="bg-[#00E7FF] hover:opacity-50 text-[#021529] text-[15px] font-medium px-4 py-2 rounded-full transition-all active:scale-95">
            dummy
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      {selectedTournament?.categories && selectedTournament.categories.length > 0 && (
        <CategoryTabs
          categories={selectedTournament.categories}
          selectedCategory={selectedCategory}
          onCategoryChange={(cat) => onCategoryChange?.(cat.label || cat)}
        />
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <ClubbieLoader />
        </div>
      ) : isEmpty ? (
        <EmptyState />
      ) : (
        <div className="flex flex-col">
          {phases.map((phase) => {
            const groupBlocks = phase.blocks.filter((b) => b.type === 'GROUP');
            const knockoutBlocks = phase.blocks.filter((b) => b.type === 'BRACKET' || b.type === 'SINGLE_MATCH');

            const isCollapsed = collapsedPhases[phase.phaseId];

            return (
              <div key={phase.phaseId} className={`space-y-4 ${isCollapsed ? "mb-2" : "mb-6"}`}>
                {/* Phase Header */}
                <div
                  className="flex items-center justify-between mb-2 bg-blue-shade-8 px-[16px] py-[8px] rounded-[16px] cursor-pointer select-none"
                  onClick={() => togglePhase(phase.phaseId)}
                >
                  <h2 className="text-[#B2B5AD] text-lg font-medium">
                    {phase.phaseName}
                  </h2>
                  <ChevronDown
                    size={20}
                    className={`text-[#B2B5AD] transition-transform ${isCollapsed ? "-rotate-180" : ""}`}
                  />
                </div>

                {!isCollapsed && (
                  <>
                    {/* Group Tables */}
                    {groupBlocks.length > 0 && (
                      <div className="space-y-6 w-full px-4 py-4 bg-blue-shade-8 rounded-2xl">
                        {groupBlocks.map((block) => (
                          <div key={block.blockId}>
                            <h2 className="text-app-text-secondary font-medium text-sm mb-4">
                              {block.blockName}
                            </h2>
                            <StandingsTableV2
                              standings={block.standings}
                              sportType={sportType}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Knockout Blocks */}
                    {knockoutBlocks.length > 0 && (
                      <div className="w-full flex flex-col gap-6 rounded-2xl p-[16px] bg-blue-shade-8 overflow-x-auto">
                        {knockoutBlocks.map((block) => {
                          if (block.type === 'BRACKET') {
                            const rounds = block.rounds || [];
                            return (
                              <div key={block.blockId} className="flex flex-col items-start gap-[12px] w-full">
                                <span className="text-[#B2B5AD] text-[14px] font-semibold">
                                  {block.blockName}
                                </span>
                                {rounds.length === 0 ? (
                                  <div className="mx-4 flex justify-center items-center text-sm text-[#7F8585] py-6 border border-dashed border-[#192634] rounded-lg w-full">
                                    No rounds available.
                                  </div>
                                ) : (
                                  <div
                                    className="flex gap-[12px] items-start overflow-x-auto w-full"
                                    style={{ scrollbarWidth: 'thin', scrollbarColor: '#1E3A5F #041A30', scrollBehavior: 'smooth' }}
                                  >
                                    {rounds.map((round) => (
                                      <div key={round.roundId} className="min-w-[296px] p-3 rounded-2xl bg-blue-shade-9">
                                        <span className="text-app-text-secondary text-[14px] mb-3 font-semibold block">
                                          {round.roundName}
                                        </span>
                                        <div className="flex flex-col gap-[8px]">
                                          {round.fixtures?.length > 0 ? (
                                            (() => {
                                              const grouped = groupFixturesByTie(round.fixtures);
                                              if (grouped) {
                                                return grouped.map((legs, tieIdx) => (
                                                  <TieCard
                                                    key={legs[0].fixtureId || tieIdx}
                                                    legs={legs}
                                                    name={legs[0].name || `Match ${tieIdx + 1}`}
                                                  />
                                                ));
                                              }
                                              return round.fixtures.map((fixture, idx) => (
                                                <FixtureCard
                                                  key={fixture.fixtureId || idx}
                                                  fixture={fixture}
                                                  name={fixture.name || `Match ${idx + 1}`}
                                                />
                                              ));
                                            })()
                                          ) : (
                                            <div className="text-[#7F8585] text-sm text-center py-6 border border-dashed border-[#192634] rounded-lg">
                                              No matches in this round.
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          if (block.type === 'SINGLE_MATCH') {
                            // Custom Matches and named SINGLE_MATCH blocks both
                            // surface here. A custom block holds N independent
                            // fixtures; render one card per fixture. Empty block
                            // (pre-link named single-match) still gets a TBD card
                            // so the block title stays visible.
                            const fixtures = block.fixtures?.length
                              ? block.fixtures
                              : [{
                                  homeTeam: null,
                                  awayTeam: null,
                                  homeTeamScore: null,
                                  awayTeamScore: null,
                                  hasResult: false,
                                }];
                            return (
                              <div
                                key={block.blockId}
                                className="flex flex-wrap gap-[14px]"
                              >
                                {fixtures.map((fixture, idx) => (
                                  <FixtureCard
                                    key={fixture.fixtureId || `${block.blockId}-${idx}`}
                                    fixture={fixture}
                                    name={fixtures.length > 1
                                      ? `${block.blockName || 'Match'} - ${idx + 1}`
                                      : (block.blockName || 'Match')}
                                  />
                                ))}
                              </div>
                            );
                          }

                          return null;
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StandingsViewV2;
